import React, { useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { loadModules } from 'esri-loader';
import BasemapGalleryWidget from './Widgets/BasemapGalleryWidget';
import LayerListWidget from './Widgets/LayerListWidget';
import CoordinateConversionWidget from './Widgets/CoordinateConversionWidget';
import ElevationProfileWidget from './Widgets/ElevationProfileWidget';
import MeasureWidget from './Widgets/MeasureWidget';
import RedlineWidget from './Widgets/RedlineWidget';
import CostEstimationWidget from './Widgets/CostEstimationWidget';
import IdentifyToggleWidget from './Widgets/IdentifyToggleWidget';
import SearchWidget from './Widgets/SearchWidget';
import BoomWMSLayer from './Layers/BoomWMSLayer';
import GeoportalWMSLayer from './Layers/GeoportalWMSLayer';
import HydrogenInfrastructureLayer from './Layers/HydrogenInfrastructureLayer';
import IEDSiteMapLayer from './Layers/IEDSiteMapLayer';
import InfrastructureWMSLayer from './Layers/InfrastructureWMSLayer';
import RasterLayer from './Layers/RasterLayer';
import { useSearchParams } from 'react-router-dom';
import CostEstimationLayer from './Layers/CostEstimationLayer';

const ArcGISMap = () => {
  const mapRef = useRef(null);
  const [view, setView] = useState(null);
  const [popupEnabled, setPopupEnabled] = useState(false);
  const [costEstimationEnabled, setCostEstimationEnabled] = useState(false);
  const location = useLocation();
  const isFullScreen = location.pathname === '/map-full';
  const [searchParams] = useSearchParams();
  const opportunity = searchParams.get('opportunity');

  useEffect(() => {
    if (view?.container) {
      if (popupEnabled) {
        view.container.style.cursor = 'crosshair';
      } else {
        view.container.style.cursor = 'default';
      }
    }
  }, [view, popupEnabled, costEstimationEnabled]);


  useEffect(() => {
    if (!view || !opportunity) return;

    view.when(() => {
      if (opportunity === 'dh2-bad-1') {
        view.goTo({
          center: [-6.19, 38.606],
          zoom: 14
        });
      }
      if (opportunity == 'dh2-sor-1') [
        view.goTo({
          center: [-2.465215, 41.760785],
          zoom: 12
        })
      ]
      if (opportunity == 'dh2-lorca') [
        view.goTo({
          center: [-1.438680, 37.729288],
          zoom: 10
        })
      ]
      if (opportunity == 'h2b2-coreses') [
        view.goTo({
          center: [-5.636238, 41.560326],
          zoom: 16
        })
      ]
      if (opportunity == 'ansasol') [
        view.goTo({
          center: [-3.393031, 38.746306],
          zoom: 16
        })
      ]
      if (opportunity == 'avalon-renovables-pbac') [
        view.goTo({
          center: [-5.674233, 36.608526],
          zoom: 9
        })
      ]      
    });
  }, [view, opportunity]);

  useEffect(() => {
    let mapView;

    loadModules(['esri/Map', 'esri/views/MapView'], {
      url: 'https://js.arcgis.com/4.34/'
    })
      .then(([Map, MapView]) => {
        const map = new Map({
          basemap: 'hybrid',
          ground: 'world-elevation'
        });

        mapView = new MapView({
          container: mapRef.current,
          map: map,
          center: [-5, 40],
          zoom: 6,
          spatialReference: {
            wkid: 102100
          }
        });

        setView(mapView);
      })
      .catch((err) => console.error(err));

    return () => {
      if (mapView) {
        mapView.destroy();
      }
    };
  }, []);

  useEffect(() => {
    if (!view) return;

    let clickHandler;

    if (!popupEnabled) {
      clickHandler = view.on('click', (event) => {
        event.stopPropagation();
        view.popup.visible = false;
        view.popup.title = null;
        view.popup.content = null;
        view.popup.features = null;
      });
    }

    return () => {
      if (clickHandler) {
        clickHandler.remove();
      }
    };
  }, [popupEnabled, view]);

  return (
    <div style={{ height: isFullScreen ? '100vh' : '90vh', width: '100%', position: 'relative' }}>
      <div id='map' ref={mapRef} style={{ height: '100%', width: '100%' }} />
      {view && <BasemapGalleryWidget view={view} />}
      {view && <LayerListWidget view={view} />}
      {view && <CoordinateConversionWidget view={view} />}
      {view && <ElevationProfileWidget view={view} />}
      {view && <MeasureWidget view={view} />}
      {view && (
        <CostEstimationWidget
          view={view}
          costEstimationEnabled={costEstimationEnabled}
          setCostEstimationEnabled={setCostEstimationEnabled}
        />
      )}
      {view && (
        <IdentifyToggleWidget
          view={view}
          popupEnabled={popupEnabled}
          setPopupEnabled={setPopupEnabled}
        />
      )}
      {view && <RasterLayer view={view} />}
      {view && <IEDSiteMapLayer view={view} />}
      {view && <GeoportalWMSLayer view={view} />}
      {view && <HydrogenInfrastructureLayer view={view} />}
      {view && <InfrastructureWMSLayer view={view} />}
      {view && <BoomWMSLayer view={view} />}
      {view && <CostEstimationLayer view={view} />}
      {view && <SearchWidget view={view} />}
      {view && <RedlineWidget view={view} />}
    </div>
  );
};

export default ArcGISMap;
